<script setup lang="ts">
  import { Icon, IconName, IconSize, Picture, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { SwitchCityField, type SwitchCity } from '@/features/switchCity';
  import type { AppDownloadStore } from '@/shared/model';
  import MobilePageHeader from '@/shared/ui/MobilePageHeader.vue';
  import MobileScreen from '@/shared/ui/MobileScreen.vue';
  import { MOBILE_MENU_LINKS } from '../config/menuLinks';
  import { MOBILE_MENU_TEXTS } from '../config/texts';

  interface MobileMenuScreenProps {
    visibleStorages: AppDownloadStore[];
    switchCities?: SwitchCity[];
    switchCurrentCity?: SwitchCity | null;
    embedded?: boolean;
    onBack: () => void;
    onOpenAccount: () => void;
    onChat: () => void;
    onDownload: (store: AppDownloadStore) => void;
    onSwitchCity: (city: SwitchCity) => void;
  }

  const props = withDefaults(defineProps<MobileMenuScreenProps>(), {
    switchCities: () => [],
    switchCurrentCity: null,
    embedded: false,
  });

  interface StoreBadge {
    store: AppDownloadStore;
    path: string;
    label: string;
    testid: string;
  }

  const storeBadges = computed<StoreBadge[]>(() => {
    const apps: StoreBadge[] = [
      { store: 'appstore', path: 'app/Appstore-bw.svg', label: 'Скачать в App Store', testid: 'mobile-menu-appstore' },
      {
        store: 'playstore',
        path: 'app/Playstore-bw.svg',
        label: 'Скачать в Google Play',
        testid: 'mobile-menu-playstore',
      },
    ];
    return apps.filter((app) => props.visibleStorages.includes(app.store));
  });
</script>

<template>
  <MobileScreen
    name="mobile-menu-screen"
    testid="search-form-mobile-menu"
    :embedded="embedded"
  >
    <MobilePageHeader
      class="mobile-menu-screen__header"
      :title="MOBILE_MENU_TEXTS.menuTitle"
      :left-icon="'close'"
      :title-variant="TypoVariant.Heading6"
      @back="onBack"
    ></MobilePageHeader>
    <div class="mobile-menu-screen__container">
      <nav
        class="mobile-menu-screen__list"
        data-testid="mobile-menu-list"
      >
        <a
          v-for="link in MOBILE_MENU_LINKS"
          :key="link.label"
          class="mobile-menu-screen__item"
          data-testid="mobile-menu-link"
          :href="link.href || undefined"
          target="_self"
        >
          <Icon
            class="mobile-menu-screen__icon"
            :name="link.icon"
            :size="IconSize.M"
          />
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ link.label }}
          </Typo>
        </a>
      </nav>

      <div class="mobile-menu-screen__account">
        <button
          type="button"
          class="mobile-menu-screen__account-item mobile-menu-screen__account-item_interactive"
          data-testid="mobile-menu-account"
          @click="onOpenAccount"
        >
          <Icon
            class="mobile-menu-screen__icon"
            :name="IconName.User"
            :size="IconSize.M"
          />
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ MOBILE_MENU_TEXTS.accountTitle }}
          </Typo>
        </button>

        <SwitchCityField
          :mobile="true"
          :redirect="false"
          :cities="switchCities"
          :current-city="switchCurrentCity"
          @change="onSwitchCity($event)"
        >
          <template #trigger="{ open, label }">
            <button
              type="button"
              class="mobile-menu-screen__account-item mobile-menu-screen__account-item_interactive"
              data-testid="mobile-menu-city"
              @click="open"
            >
              <Icon
                class="mobile-menu-screen__icon"
                :name="IconName.Pin"
                :size="IconSize.M"
              />
              <Typo
                :variant="TypoVariant.BodyLRegular"
                tag="span"
              >
                {{ label }}
              </Typo>
            </button>
          </template>
        </SwitchCityField>

        <button
          type="button"
          class="mobile-menu-screen__account-item mobile-menu-screen__account-item_interactive"
          data-testid="mobile-menu-chat"
          @click="onChat"
        >
          <Icon
            class="mobile-menu-screen__icon"
            :name="IconName.MessageDots"
            :size="IconSize.M"
          />
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ MOBILE_MENU_TEXTS.chat }}
          </Typo>
        </button>

        <div class="mobile-menu-screen__stores">
          <button
            v-for="badge in storeBadges"
            :key="badge.store"
            type="button"
            class="mobile-menu-screen__store-btn"
            :aria-label="badge.label"
            :data-testid="badge.testid"
            @click="onDownload(badge.store)"
          >
            <Picture
              :path="badge.path"
              :lazy="false"
              :extensions="[]"
              :high-resolution="false"
              fit="contain"
              class="mobile-menu-screen__store"
            />
          </button>
        </div>
      </div>
    </div>
  </MobileScreen>
</template>

<style scoped lang="scss">
  .mobile-menu-screen {
    background: var(--color-bg-primary);

    &__container {
      overflow: auto;
    }

    &__stores {
      display: flex;
      gap: var(--spacing-s);
      margin-top: var(--spacing-xs);
    }

    &__store-btn {
      background: none;
      border: none;
      cursor: pointer;
      display: inline-flex;
      height: 44px;
      padding: 0;
      width: 166px;
    }

    &__header {
      box-sizing: border-box;
      flex: 0 0 auto;
      padding: var(--spacing-s) var(--spacing-m) var(--spacing-xs) var(--spacing-m);
    }

    &__list {
      box-sizing: border-box;
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      padding: var(--spacing-m);
    }

    &__item {
      align-items: center;
      color: var(--color-text-primary);
      display: flex;
      gap: var(--spacing-xs);
      min-height: 48px;
      text-decoration: none;
    }

    &__icon {
      fill: var(--color-text-secondary);
      flex: 0 0 auto;
    }

    &__account {
      background: var(--color-bg-secondary);
      box-sizing: border-box;
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      gap: var(--spacing-m);
      padding: var(--spacing-l) var(--spacing-m);
    }

    &__account-item {
      align-items: center;
      background: none;
      border: none;
      color: var(--color-text-secondary);
      cursor: default;
      display: flex;
      gap: var(--spacing-xs);
      min-height: auto;
      padding: 0;
      text-align: left;
      text-decoration: none;
      width: 100%;
    }

    &__account-item_interactive {
      cursor: pointer;
    }
  }
</style>
